import { FaGithub, FaGitlab } from "react-icons/fa6";
import type { IconType } from "react-icons";
import {
    EventCategory,
    FindingType,
    PermissionLevel,
    RiskLevel,
    SaasConnection,
    SaasProvider,
    SyncStatus,
    TimeRange,
} from "./saasTypes";

export const PAGE_SIZE = 7;

export interface ProviderMeta {
    label: string;
    icon: IconType;
    resourceLabel: string;
    resourceSingular: string;
    groupLabel: string;
    accountLabel: string;
}

export const PROVIDERS: Record<SaasProvider, ProviderMeta> = {
    github: {
        label: "GitHub",
        icon: FaGithub,
        resourceLabel: "Repositories",
        resourceSingular: "Repository",
        groupLabel: "Teams",
        accountLabel: "Organization",
    },
    gitlab: {
        label: "GitLab",
        icon: FaGitlab,
        resourceLabel: "Projects",
        resourceSingular: "Project",
        groupLabel: "Groups",
        accountLabel: "Account",
    },
};

export const RANGE_OPTIONS = [
    { value: "24h", label: "Last 24 hours", hours: 24 },
    { value: "7d", label: "Last 7 days", hours: 24 * 7 },
    { value: "30d", label: "Last 30 days", hours: 24 * 30 },
    { value: "90d", label: "Last 90 days", hours: 24 * 90 },
] as const;

export type RangeKey = (typeof RANGE_OPTIONS)[number]["value"];

export const toTimeRange = (key: RangeKey): TimeRange => {
    const hours = RANGE_OPTIONS.find((option) => option.value === key)?.hours ?? 24 * 30;
    const to = new Date();
    to.setSeconds(0, 0);
    const from = new Date(to.getTime() - hours * 3600 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
};

export const parseUtc = (value?: string | null): Date | null => {
    if (!value) return null;
    const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value?: string | null, fallback = "—") => {
    const date = parseUtc(value);
    return date
        ? date.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
        : fallback;
};

export const formatDate = (value?: string | null, fallback = "—") => {
    const date = parseUtc(value);
    return date ? date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : fallback;
};

export const formatRelative = (value?: string | null, fallback = "Never") => {
    const date = parseUtc(value);
    if (!date) return fallback;
    const seconds = Math.round((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return "Just now";
    const units: [number, string][] = [
        [60, "minute"],
        [3600, "hour"],
        [86400, "day"],
        [86400 * 30, "month"],
        [86400 * 365, "year"],
    ];
    let label = "minute";
    let size = 60;
    for (const [unitSize, unitLabel] of units) {
        if (seconds >= unitSize) {
            size = unitSize;
            label = unitLabel;
        }
    }
    const count = Math.floor(seconds / size);
    return `${count} ${label}${count === 1 ? "" : "s"} ago`;
};

export const formatBucket = (value: string, interval: "hour" | "day" | "week") => {
    const date = parseUtc(value);
    if (!date) return value;
    return interval === "hour"
        ? date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
        : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const formatNumber = (value?: number | null) => (value ?? 0).toLocaleString();

export const humanize = (value?: string | null) =>
    (value ?? "")
        .toLowerCase()
        .split(/[_.\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

export const CATEGORY_LABELS: Record<EventCategory, string> = {
    AUTHENTICATION: "Authentication",
    ACCESS_MANAGEMENT: "Access management",
    REPOSITORY_ADMINISTRATION: "Repository admin",
    BRANCH_PROTECTION: "Branch protection",
    SECURITY_SETTINGS: "Security settings",
    SECRETS_AND_TOKENS: "Secrets & tokens",
    INTEGRATIONS: "Integrations",
    CI_CD: "CI/CD",
    CODE_CHANGE: "Code changes",
    CODE_REVIEW: "Code review",
    DATA_ACCESS: "Data access",
    ISSUE_TRACKING: "Issues",
    COLLABORATION: "Collaboration",
    OTHER: "Other",
};

export const CATEGORY_COLORS: Record<string, string> = {
    AUTHENTICATION: "#0ea5e9",
    ACCESS_MANAGEMENT: "#8b5cf6",
    REPOSITORY_ADMINISTRATION: "#ef4444",
    BRANCH_PROTECTION: "#f97316",
    SECURITY_SETTINGS: "#dc2626",
    SECRETS_AND_TOKENS: "#e11d48",
    INTEGRATIONS: "#d946ef",
    CI_CD: "#14b8a6",
    CODE_CHANGE: "#3b82f6",
    CODE_REVIEW: "#6366f1",
    DATA_ACCESS: "#f59e0b",
    ISSUE_TRACKING: "#22c55e",
    COLLABORATION: "#84cc16",
    OTHER: "#94a3b8",
};

export const categoryLabel = (value?: string | null) =>
    value && value in CATEGORY_LABELS ? CATEGORY_LABELS[value as EventCategory] : humanize(value) || "Other";

export const FINDING_LABELS: Record<FindingType, string> = {
    DORMANT_IDENTITY: "Dormant account",
    DORMANT_PRIVILEGED_IDENTITY: "Dormant privileged account",
    UNUSED_ADMIN_PRIVILEGE: "Unused admin access",
    UNUSED_WRITE_PRIVILEGE: "Unused write access",
    TWO_FACTOR_DISABLED: "Two-factor disabled",
    TWO_FACTOR_NOT_ENFORCED: "Two-factor not enforced",
    OUTSIDE_COLLABORATOR: "Outside collaborator",
    OUTSIDE_COLLABORATOR_ADMIN: "Outside collaborator with admin",
    PUBLIC_RESOURCE: "Public resource",
    UNPROTECTED_DEFAULT_BRANCH: "Unprotected default branch",
    EXCESSIVE_OWNERS: "Too many owners",
    BLOCKED_IDENTITY_WITH_ACCESS: "Blocked account with access",
    ACTIVITY_ALERT: "High-risk activity",
    ACTIVITY_BURST: "Unusual activity burst",
};

export const findingLabel = (value?: string | null) =>
    value && value in FINDING_LABELS ? FINDING_LABELS[value as FindingType] : humanize(value);

export const RISK_ORDER: RiskLevel[] = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"];

export const RISK_STYLES: Record<RiskLevel, string> = {
    CRITICAL: "border-red-300 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300",
    HIGH: "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-300",
    MEDIUM: "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300",
    LOW: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300",
    INFO: "border-border bg-card text-muted-foreground",
};

export const RISK_COLORS: Record<RiskLevel, string> = {
    CRITICAL: "#dc2626",
    HIGH: "#f97316",
    MEDIUM: "#f59e0b",
    LOW: "#3b82f6",
    INFO: "#94a3b8",
};

export const PERMISSION_STYLES: Record<PermissionLevel, string> = {
    OWNER: "border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300",
    ADMIN: "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-300",
    MAINTAIN: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300",
    WRITE: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300",
    TRIAGE: "border-border bg-card text-foreground",
    READ: "border-border bg-card text-foreground",
    NONE: "border-border bg-card text-muted-foreground",
};

export type ConnectionTone = "healthy" | "working" | "warning" | "error";

export interface ConnectionHealth {
    tone: ConnectionTone;
    label: string;
    detail: string;
}

const SYNC_LABELS: Partial<Record<SyncStatus, string>> = {
    IN_PROGRESS: "Syncing inventory",
    SYNC_COMPLETED: "Waiting for log history",
    FETCHING_HISTORICAL_LOGS: "Collecting 30-day history",
    COMPLETED: "Monitoring",
    MANUAL_SYNC_COMPLETED: "Monitoring",
    FAILED: "Sync failed",
};

export const connectionHealth = (connection: SaasConnection): ConnectionHealth => {
    if (connection.credentialState === "EXPIRED") {
        return { tone: "error", label: "Reconnect required", detail: connection.credentialReason ?? "Credentials were rejected" };
    }
    if (connection.syncRunning || connection.syncStatus === "IN_PROGRESS") {
        return { tone: "working", label: "Syncing inventory", detail: `Started ${formatRelative(connection.syncStartedAt)}` };
    }
    if (connection.syncStatus === "FAILED") {
        return { tone: "error", label: "Sync failed", detail: connection.lastSyncError ?? "The last sync did not finish" };
    }
    if (connection.syncStatus === "SYNC_COMPLETED" || connection.syncStatus === "FETCHING_HISTORICAL_LOGS") {
        return {
            tone: "working",
            label: SYNC_LABELS[connection.syncStatus] ?? "Preparing",
            detail: connection.lastLogFetchError ?? "Analytics fill in as the history is collected",
        };
    }
    if (connection.lastLogFetchError) {
        return { tone: "warning", label: "Monitoring with issues", detail: connection.lastLogFetchError };
    }
    if (connection.lastSyncError) {
        return { tone: "warning", label: "Monitoring", detail: connection.lastSyncError };
    }
    return {
        tone: "healthy",
        label: connection.syncStatus ? SYNC_LABELS[connection.syncStatus] ?? humanize(connection.syncStatus) : "Connected",
        detail: `Logs collected until ${formatDateTime(connection.logsCollectedUntil, "pending")}`,
    };
};

export const LOG_SOURCE_LABELS: Record<string, string> = {
    AUDIT_LOG: "Audit log",
    EVENTS: "Activity events",
    AUDIT_LOG_AND_EVENTS: "Audit log + activity events",
};

export const accountDisplay = (connection: SaasConnection) =>
    connection.accountName && connection.accountName !== connection.accountLogin
        ? `${connection.accountName} (${connection.accountLogin})`
        : connection.accountLogin ?? connection.accountId;
